import type { DirectiveBinding, Directive } from "vue";

type Opts = {
  text?: string | null;              // target finale; se falsy => resta in loop
  length?: number;                   // lunghezza placeholder in loop quando text è falsy
  alphabet?: string;                 // set di caratteri; default ASCII stampabile
  stepMs?: [number, number];         // intervallo ms tra step in loop (random in range)
  cycles?: [number, number];         // numero “hop” casuali prima del target in finale
  durFinal?: [number, number];       // durata totale per colonna nella composizione finale
  gapEm?: number;                    // gap orizzontale tra colonne
  xfadeMs?: number;                  // durata crossfade ghost→finale
  blurPx?: number;                   // blur durante rotazione; 0 = nitido
  seed?: number;                     // seme opzionale per riproducibilità
  reshuffleMs?: [number, number];    // intervallo per rimiscolare la sequenza in loop
};

// ASCII stampabile (spazio escluso per scelta intenzionale: split-flap spesso mostra set compatti)
// Aggiungere lo spazio se necessario nel proprio alphabet.
const DEFAULT_ASCII =
  '!"#$%&\'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\\]^_`abcdefghijklmnopqrstuvwxyz{|}~';

// Inietta CSS una sola volta per istanza app; evita dipendenze esterne
const injectOnce = (() => {
  let done = false;
  return () => {
    if (done) return; done = true;
    const css = `
.sf-row{display:inline-flex;gap:var(--sf-gap,0.06em);position:relative;perspective:600px}
.sf-col{display:inline-block;position:relative;height:1em;width:var(--w,auto);transition:width 80ms linear}
.sf-viewport{display:block;height:1em;overflow:hidden;position:relative}
.sf-stack{display:block;line-height:1;will-change:transform,filter,opacity}
.sf-glyph{display:block;height:1em;line-height:1;white-space:pre}
.sf-ghost{position:absolute;inset:0;pointer-events:none;opacity:.6}
.sf-fade{animation:sf-fade var(--xfade,300ms) ease-out forwards}
@keyframes sf-fade{to{opacity:0}}`;
    const el = document.createElement("style");
    el.textContent = css;
    document.head.appendChild(el);
  };
})();

// Utility random grezze per fallback
const ri = (a:number,b:number)=>Math.floor(a+Math.random()*(b-a+1));
const rr = (a:number,b:number)=>a+Math.random()*(b-a);

// PRNG xorshift32: economico, deterministico, sufficiente per variazioni visive
function makeRng(seed: number){
  let s = seed || 88675123;
  const uint = ()=>{ s ^= s << 13; s ^= s >>> 17; s ^= s << 5; return (s>>>0); };
  return {
    int:(min:number,max:number)=>min + (uint() % (max-min+1)),
    float:()=>uint()/0xffffffff
  };
}

// Fisher–Yates con PRNG custom per evitare pattern di Math.random()
function shuffle<T>(arr:T[], rnd: ReturnType<typeof makeRng>){
  for(let i=arr.length-1;i>0;i--){ const j=rnd.int(0,i); [arr[i],arr[j]]=[arr[j],arr[i]]; }
  return arr;
}

// Passo di avanzamento “coprimo” per evitare cicli corti che ripassano sugli stessi indici
function coprimeStep(n:number, rnd:ReturnType<typeof makeRng>){
  const gcd=(a:number,b:number)=>b?gcd(b,a%b):a;
  let step = rnd.int(1,4); // passi piccoli per step visivo fine
  let tries = 0;
  while (gcd(step,n) !== 1 && tries < 10){ step = rnd.int(1,4); tries++; }
  return step;
}

// Misuratore larghezze per-glyph con cache; copia lo stile del contenitore per coerenza tipografica
class WidthMeter {
  private cache = new Map<string, number>();
  private probe: HTMLSpanElement;
  constructor(sampleEl: HTMLElement){
    this.probe = document.createElement("span");
    const cs = getComputedStyle(sampleEl);
    Object.assign(this.probe.style, {
      position:"absolute", visibility:"hidden", whiteSpace:"pre", lineHeight:"1",
      font: cs.font, letterSpacing: cs.letterSpacing, fontFeatureSettings: cs.fontFeatureSettings as any
    });
    this.probe.textContent = " ";
    document.body.appendChild(this.probe);
  }
  width(ch: string){
    if (this.cache.has(ch)) return this.cache.get(ch)!;
    this.probe.textContent = ch || " ";
    const w = this.probe.getBoundingClientRect().width; // misura reale in px
    this.cache.set(ch, w);
    return w;
  }
}

type Animator = { stop: ()=>void };

// Costruisce una colonna con una sequenza predefinita (senza animazioni)
function makeColumn(col: HTMLElement, seq: string[]){
  const vp  = document.createElement("span"); vp.className = "sf-viewport";
  const st  = document.createElement("span"); st.className = "sf-stack";
  seq.forEach(c=>{ const g=document.createElement("span"); g.className="sf-glyph"; g.textContent=c; st.appendChild(g); });
  vp.appendChild(st); col.appendChild(vp);
  return { st };
}

// Loop infinito: nessun rebuild del nodo animato; reshuffle in-place per evitare perdita di timer
function animateLoop(
  col: HTMLElement,
  alphabet: string,
  meter: WidthMeter,
  stepMs:[number,number],
  blurPx:number,
  rng: ReturnType<typeof makeRng>,
  reshuffleMs:[number,number]
): Animator {
  // Inizializza sequenza casuale
  let seq = alphabet.split("");
  for(let i=seq.length-1;i>0;i--){ const j=rng.int(0,i); [seq[i],seq[j]]=[seq[j],seq[i]]; }

  // Crea una sola volta tutti i glyph; evita GC e rimbalzi layout
  const vp  = document.createElement("span"); vp.className = "sf-viewport";
  const st  = document.createElement("span"); st.className = "sf-stack";
  const glyphEls: HTMLSpanElement[] = [];
  for (const c of seq){
    const g = document.createElement("span");
    g.className = "sf-glyph";
    g.textContent = c;
    glyphEls.push(g);
    st.appendChild(g);
  }
  vp.appendChild(st);
  col.appendChild(vp);

  st.style.setProperty("--blur", `${blurPx}px`);

  // Stato animazione: indice corrente, direzione, passo coprimo
  let idx = rng.int(0, seq.length-1);
  const dir = rng.int(0,1) ? 1 : -1;
  const gcd=(a:number,b:number)=>b?gcd(b,a%b):a;
  let step = (()=>{ let s=rng.int(1,4); let t=0; while(gcd(s,seq.length)!==1 && t++<10) s=rng.int(1,4); return s; })();

  // Aggiorna vista: translate per “rotazione” e filtro per profondità
  const setView = (i:number)=>{
    const ch = seq[i];
    col.style.setProperty("--w", `${meter.width(ch)}px`);                 // larghezza per-glyph (fisarmonica)
    st.style.transform = `translateY(${-i}em) rotateX(${i===0?0:12}deg)`; // rotazione leggera per effetto meccanico
    st.style.filter = i===0? "blur(0px)" : `blur(${blurPx}px)`;           // blur sulle transizioni
  };

  let dead = false;
  let timer:any;
  let nextReshuffleAt = performance.now() + rr(reshuffleMs[0], reshuffleMs[1]);

  // Rimescola la sequenza SENZA ricreare i nodi; mantiene coerenza temporale e loop stabile
  const reshuffleInPlace = ()=>{
    const current = seq[idx];                          // preserva glyph visibile
    for(let i=seq.length-1;i>0;i--){                   // Fisher–Yates
      const j=rng.int(0,i);
      [seq[i],seq[j]]=[seq[j],seq[i]];
    }
    const newPos = seq.indexOf(current);               // riallinea idx al glyph corrente
    if (newPos !== -1) idx = newPos;
    for (let i=0;i<glyphEls.length;i++) glyphEls[i].textContent = seq[i]; // aggiorna testo in-place
    // nuovo passo coprimo per rompere eventuali pattern
    let s=rng.int(1,4), t=0; while(gcd(s,seq.length)!==1 && t++<10) s=rng.int(1,4);
    step = s;
  };

  // Tick temporizzato: usa setTimeout + rAF per jitter naturale e sincronizzazione paint
  const tick = ()=>{
    if (dead) return;

    idx = (idx + dir*step + seq.length) % seq.length;
    setView(idx);

    if (performance.now() >= nextReshuffleAt){
      reshuffleInPlace();                               // varia la sequenza periodicamente
      nextReshuffleAt = performance.now() + rr(reshuffleMs[0], reshuffleMs[1]);
      setView(idx);                                     // riallinea dopo reshuffle
    }

    timer = window.setTimeout(()=>requestAnimationFrame(tick), rr(stepMs[0], stepMs[1]));
  };

  // Init
  col.style.setProperty("--w", `${meter.width(seq[idx])}px`);
  setView(idx);
  timer = window.setTimeout(()=>requestAnimationFrame(tick), rr(stepMs[0], stepMs[1]));

  return { stop(){ dead = true; clearTimeout(timer); } };
}

// Composizione finale: sequenza di hop casuali + target; tempo totale fisso per colonna
function animateFinal(
  col: HTMLElement,
  alphabet: string,
  target: string,
  meter: WidthMeter,
  cycles:[number,number],
  durRange:[number,number],
  blurPx:number,
  rng: ReturnType<typeof makeRng>
): Animator {
  // Costruisci la sequenza: caratteri casuali (hop) seguiti dal target
  const hops = rng.int(cycles[0], cycles[1]);
  const seq: string[] = Array.from({length:hops}, ()=>alphabet[rng.int(0,alphabet.length-1)]);
  seq.push(alphabet.includes(target) ? target : " ");   // fallback a spazio se target non presente

  const { st } = makeColumn(col, seq);
  let i = 0, dead=false;

  // Durata per-step derivata da durata totale; garantisce arrivo simultaneo tra colonne con differenze minime
  const total = rr(durRange[0], durRange[1]);
  const per = total / Math.max(1, seq.length-1);

  const step = ()=>{
    if (dead) return;
    const ch = seq[i];
    col.style.setProperty("--w", `${meter.width(ch)}px`);                   // aggiorna larghezza colonna
    st.style.transform = `translateY(${-i}em) rotateX(${i===seq.length-1?0:12}deg)`; // riduce rotazione sul target
    st.style.filter = i===seq.length-1 ? "blur(0px)" : `blur(${blurPx}px)`; // rimuove blur al termine
    if (i < seq.length-1){ i++; window.setTimeout(()=>requestAnimationFrame(step), per); }
  };

  // Init
  col.style.setProperty("--w", `${meter.width(seq[0])}px`);
  requestAnimationFrame(step);
  return { stop(){ dead = true; } };
}

// Dispatcher: decide loop o finale e gestisce ghost fade
function run(el: HTMLElement, v: Opts | string | null){
  injectOnce();

  // Normalizza opzioni; stringa semplice => { text: string }
  const opts: Opts = typeof v === "string" || v == null ? { text: v as any } : v;

  // Parametri con default robusti
  const alphabet = opts.alphabet ?? DEFAULT_ASCII;
  const gapEm = opts.gapEm ?? 0.06;
  const stepMs = opts.stepMs ?? [60, 140];
  const cycles = opts.cycles ?? [2,5];
  const durFinal = opts.durFinal ?? [450,900];
  const xfadeMs = opts.xfadeMs ?? 220;   // fade del ghost: piccolo per non “sporcare” il roll finale
  const blurPx = opts.blurPx ?? 0;       // blur disattivato di default per testo nitido
  const reshuffleMs = opts.reshuffleMs ?? [1200, 2400];

  // Contesto visivo
  el.classList.add("sf-row");
  el.style.setProperty("--sf-gap", `${gapEm}em`);
  el.setAttribute("aria-busy", opts.text ? "false" : "true");

  // Misuratore legato allo stile del contenitore (font, letter-spacing, ecc)
  const meter = new WidthMeter(el);

  // Stato logico
  const hasTarget = !!(opts.text && opts.text.length);
  const len = hasTarget ? opts.text!.length : (opts.length ?? 8);

  // PRNG per-colonna: baseSeed variabile nel tempo; offset per indice colonna
  const baseSeed = (opts.seed ?? Math.floor(Math.random()*1e9)) ^ Date.now();

  // Ghost per transizioni loop→finale: clona DOM corrente e sfuma
  const wasLoop = el.dataset.state === "loop";
  let ghost: HTMLElement | null = null;
  if (wasLoop){
    ghost = el.cloneNode(true) as HTMLElement;
    ghost.classList.add("sf-ghost","sf-fade");
    ghost.style.setProperty("--xfade", `${xfadeMs}ms`);
  }

  // Stop sicuro di eventuali animazioni precedenti
  (el as any).__sf_animators?.forEach((a:Animator)=>a.stop());
  (el as any).__sf_animators = [];

  // Ricostruzione contenuto: semplice e controllabile
  el.innerHTML = "";
  const anims: Animator[] = [];

  if (!hasTarget){
    // Loop infinito: una colonna per lunghezza richiesta
    for(let i=0;i<len;i++){
      const col = document.createElement("span"); col.className="sf-col";
      el.appendChild(col);
      const rng = makeRng(baseSeed + i*2654435761); // moltiplicatore di Knuth per dispersione dei semi
      const a = animateLoop(col, alphabet, meter, stepMs, blurPx, rng, reshuffleMs);
      anims.push(a);
    }
    el.dataset.state = "loop";
  } else {
    // Finale: una colonna per carattere del target
    for(let i=0;i<opts.text!.length;i++){
      const col = document.createElement("span"); col.className="sf-col";
      el.appendChild(col);
      const rng = makeRng(baseSeed + i*2654435761);
      const a = animateFinal(col, alphabet, opts.text![i], meter, cycles, durFinal, blurPx, rng);
      anims.push(a);
    }
    el.dataset.state = "final";
  }

  // Applica ghost se presente per crossfade non invasivo
  if (ghost) { el.appendChild(ghost); setTimeout(()=>ghost && ghost.remove(), xfadeMs+30); }

  // Conserva riferimenti per stop successivi
  (el as any).__sf_animators = anims;
}

// Direttiva Vue
const vSplitFlap: Directive = {
  mounted(el, b: DirectiveBinding<Opts | string | null>){ run(el, b.value); },
  updated(el, b: DirectiveBinding<Opts | string | null>){ run(el, b.value); }
};

export default vSplitFlap;
